<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('organization.enabled_domains') }}
      </p>
      <p class="mt-2 pa-0 grey--text text--darken-2">
        {{ $t('organization_settings.domains_subtitle') }}
      </p>
    </div>
    <v-form ref="domains" v-on:submit.prevent v-model="createForm.isValid">
      <v-row>
        <v-col cols="12">
          <v-text-field
            outlined
            required
            dense
            v-model="createForm.domainToAdd"
            data-testid="domains-textField-domainToAdd"
            data-tourid="domains-textField-domainToAdd"
            :rules="createForm.domainRules"
            :label="`${$t('organization_settings.domain_name')}*`"
            :placeholder="`${$t('common.example')}.com`"
            @keyup.enter.prevent="addDomain"
          ></v-text-field>
        </v-col>
      </v-row>
      <div class="mt-n2 text-right">
        <Button
          size="large"
          color="primary"
          data-testid="domains-button-addDomain"
          data-tourid="domains-button-addDomain"
          :loading="isLoading"
          :disabled="shouldDisableAddDomainButton"
          :text="$t('organization_settings.add_domain')"
          @click="addDomain"
        />
      </div>
    </v-form>
    <div class="mt-4" v-if="domainsToShow.length">
      <p class="h6 mb-3 mt-0">
        {{ $t('organization_settings.added_domains') }}
      </p>
      <template v-for="(domain, index) in domainsToShow">
        <v-hover v-slot:default="{ hover }" :key="index">
          <div
            v-if="domain.isViewMode"
            data-testid="domains-div-addedDomain"
            class="pa-2 d-flex justify-space-between align-center"
            :class="{
              hover: hover && domain.isViewMode,
              grey: hover && domain.isViewMode,
              'lighten-5': hover && domain.isViewMode,
            }"
          >
            <p
              class="ma-0"
              :data-testid="`domains-paragraph-addedDomains${index}`"
            >
              {{ domain.name }}
            </p>
            <div class="d-flex align-center justify-space-between">
              <v-icon
                v-for="icon in view_mode_icons"
                class="grey--text icon"
                :data-testid="`domains-icon-${icon.type}${index}`"
                :key="icon.type"
                :disabled="disableActions"
                @click="handleClick(icon.type, domain)"
              >
                {{ icon.name }}
              </v-icon>
            </div>
          </div>
          <v-form
            v-else
            ref="editDomains"
            v-on:submit.prevent
            v-model="editForm.isValid"
          >
            <div class="pa-2 d-flex justify-space-between align-center">
              <v-text-field
                outlined
                required
                dense
                autofocus
                hide-details="auto"
                v-model="domain.name"
                data-testid="domains-textField-domainToEdit"
                :rules="editForm.domainRules(index)"
                @keyup.enter.prevent="editDomain(domain, index)"
              >
                <template v-slot:append>
                  <div class="d-flex me-2">
                    <v-icon
                      v-for="icon in edit_mode_icons"
                      class="grey--text icon"
                      :data-testid="`domains-icon-${icon.type}`"
                      :loading="isSaving"
                      :key="icon.type"
                      :disabled="
                        isLoading ||
                          (!editForm.isValid && icon.type !== 'cancel')
                      "
                      @click="handleClick(icon.type, domain, index)"
                    >
                      {{ icon.name }}
                    </v-icon>
                  </div>
                </template>
              </v-text-field>
            </div>
          </v-form>
        </v-hover>
      </template>
    </div>
    <DeleteConfirmationModal
      confirmTestID="domains-button-delete"
      cancelTestID="domains-button-cancelDelete"
      :show="showDeleteModal"
      :isLoading="isLoading"
      :title="$t('organization_settings.delete_domain_title')"
      :text="$t('organization_settings.delete_confirmation')"
      @cancel="showDeleteModal = false"
      @delete="removeDomain"
    />
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'Domains',

  mixins: [mixin],

  components: {
    DeleteConfirmationModal,
    Button,
  },

  props: {
    organization: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      createForm: {
        isValid: false,
        domainRules: this.createDomainRules(),
        domainToAdd: '',
      },
      editForm: {
        isValid: false,
        domainRules: this.editDomainRules,
      },
      isSaving: false,
      showDeleteModal: false,
      organizationId: this.getOrganizationId(),
      disableActions: false,
      view_mode_icons: [
        { type: 'edit', name: 'create' },
        { type: 'delete', name: 'mdi-delete-outline' },
      ],
      edit_mode_icons: [
        { type: 'save', name: 'done' },
        { type: 'cancel', name: 'clear' },
      ],
    }
  },

  computed: {
    shouldDisableAddDomainButton() {
      return this.isLoading || !this.createForm.isValid
    },
    domainsToShow() {
      const domains = this.organization?.domains ?? []
      return (
        domains.map(domain => ({
          name: domain,
          isViewMode: true,
        })) || []
      )
        .slice()
        .reverse()
    },
  },

  methods: {
    createDomainRules() {
      return [
        d =>
          !this.organization?.domains?.includes(d) ||
          this.$t('organization.domain_is_duplicated', {
            domainName: d,
          }),
        d => this.domainRules(d),
      ]
    },

    editDomainRules(index) {
      return [
        d =>
          !this.organization?.domains?.includes(d) ||
          d === this.organization?.domains?.slice().reverse()[index] ||
          this.$t('organization.domain_is_duplicated', {
            domainName: d,
          }),
        d => this.domainRules(d),
      ]
    },

    addDomain() {
      if (this.createForm.isValid) {
        let updatedDomains = Array.from(this.organization?.domains)
        updatedDomains.push(this.createForm.domainToAdd)

        this.$emit('update', { domains: updatedDomains })
        this.$refs.domains.reset()
      }
    },

    editDomain(domain, index) {
      if (this.editForm.isValid) {
        const domains = Array.from(this.organization?.domains)
        const reversedDomains = Array.from(domains)
          .slice()
          .reverse()
        const oldDomain = reversedDomains[index]
        const indexToUpdate = domains.findIndex(d => d === oldDomain)
        domains[indexToUpdate] = domain.name
        this.$emit('update', { domains: domains })
        this.disableActions = false
      }
    },

    removeDomain() {
      const updatedDomains = Array.from(this.organization?.domains).filter(
        domain => domain !== this.domainToDelete.name
      )

      this.$emit('update', { domains: updatedDomains })
      this.showDeleteModal = false
    },

    handleClick(type, domain, domainIndex) {
      switch (type) {
        case 'edit':
          this.disableActions = true
          domain.isViewMode = false
          break
        case 'delete':
          this.domainToDelete = domain
          this.showDeleteModal = true
          break
        case 'save':
          this.editDomain(domain, domainIndex)
          break
        case 'cancel':
          this.isSaving = true
          domain.name = this.organization?.domains.splice().reverse()[
            domainIndex
          ]
          domain.isViewMode = true
          this.disableActions = false
          this.isSaving = false
          break
      }
    },

    checkDomainValidity(d) {
      return (
        /(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{0,62}[a-zA-Z0-9]\.)+[a-zA-Z]{2,63}$)/.test(
          d
        ) || this.$t('organization.domain_is_not_valid')
      )
    },

    checkDomainDuplicated(d) {
      return (
        !this.$constants.organization.domainsBlacklist.includes(d) ||
        this.$t('organization.domain_is_insecure_to_add', {
          domainName: d,
        })
      )
    },

    checkDomainWWW(d) {
      return !d?.includes('www') || this.$t('organization.domain_has_www')
    },

    domainRules(d) {
      const validity = this.checkDomainValidity(d)
      const duplicated = this.checkDomainDuplicated(d)
      const hasWWW = this.checkDomainWWW(d)

      if (typeof validity === 'string') return validity
      if (typeof duplicated === 'string') return duplicated
      if (typeof hasWWW === 'string') return hasWWW
      return !!d
    },
  },
}
</script>
<style lang="scss" scoped>
.hover {
  border-radius: 4px;
}
.icon {
  &:last-child:not(:first-child) {
    margin-left: 20px !important;
  }
}
</style>
